import React, { useState, useEffect, useRef } from "react";
import Checkbox from "../Checkbox";

interface ConfirmationModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  showDoNotShowAgain?: boolean;
  onDoNotShowAgainChange?: (checked: boolean) => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  showDoNotShowAgain = false,
  onDoNotShowAgainChange,
}) => {
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // Prevent body scroll and block interactions while modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Add a class to the body to indicate modal is open
    document.body.setAttribute("data-modal-open", "true");

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.removeAttribute("data-modal-open");
    };
  }, []);

  useEffect(() => {
    const focusFirstElement = () => {
      confirmButtonRef.current?.focus();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!modalRef.current) return;

      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
        return;
      }

      if (event.key === "Enter") {
        const activeElement = document.activeElement as HTMLElement | null;
        if (!activeElement || !modalRef.current.contains(activeElement)) return;

        if (
          activeElement === confirmButtonRef.current ||
          activeElement === cancelButtonRef.current
        ) {
          return;
        }

        const tagName = activeElement.tagName.toLowerCase();
        if (!["input", "textarea", "select", "button"].includes(tagName)) {
          onConfirm();
        }
        return;
      }

      if (event.key !== "Tab") return;

      const focusableSelector =
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";
      const focusableElements = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter(
        (el) =>
          !el.hasAttribute("disabled") &&
          el.getAttribute("aria-hidden") !== "true",
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement | null;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    const animationFrameId = window.requestAnimationFrame(focusFirstElement);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onCancel, onConfirm]);

  return (
    <div
      data-modal-overlay
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-md"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        data-modal-overlay
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-modal-title"
        aria-describedby="confirmation-modal-message"
        className="relative bg-[#FBF8F1] border border-[#DED8CC] rounded-[8px] shadow-xl w-[90%] max-w-[500px] h-auto p-6 mx-4 z-10"
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
      >
        {/* Warning Icon */}
        <div className="flex justify-center mb-5 mt-1">
          <img
            src="/assets/warning.svg"
            alt="Warning"
            className="w-[121px] h-[121px]"
          />
        </div>

        {/* Title */}
        <h2
          id="confirmation-modal-title"
          className="text-center text-[#294B39] font-mulish font-bold text-[25px] leading-[100%] mb-4"
        >
          {title}
        </h2>

        {/* Message */}
        <p
          id="confirmation-modal-message"
          className="text-center text-[#687368] font-mulish text-[20px] font-[600] leading-[120%] mb-6 mx-1"
        >
          {message}
        </p>

        {/* Buttons - Cancel on left, Confirm on right */}
        <div className={`flex gap-3 ${showDoNotShowAgain ? "mb-6" : ""}`}>
          <button
            onClick={onCancel}
            ref={cancelButtonRef}
            className="flex-1 py-4 px-4 bg-[#FBF8F1] text-[#28543D] text-[20px] font-[600] hover:bg-[#EEF0E3] focus:bg-[#E2E9DC] border border-[#D4D3C5] rounded-[6px] transition-colors duration-200"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            ref={confirmButtonRef}
            className="flex-1 py-4 px-4 bg-[#B83A30] hover:bg-[#9F3129] text-white text-[20px] font-[600] rounded-[6px] transition-colors duration-200"
          >
            {confirmLabel}
          </button>
        </div>

        {/* Checkbox */}
        {showDoNotShowAgain && (
          <div className="flex justify-center">
            <Checkbox
              checked={doNotShowAgain}
              onChange={(checked) => {
                setDoNotShowAgain(checked);
                onDoNotShowAgainChange?.(checked);
              }}
            >
              <span className="text-sm text-[#252525]">
                Do not show this popup again
              </span>
            </Checkbox>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmationModal;
